import "@/App.css";
import "@/index.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UpgradeBannerProvider } from "@/components/UpgradeBanner";
import { Toaster } from "sonner";

const TOASTER_OPTIONS = { className: "font-mono rounded-none border-2 border-black" };

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DashboardLayout from "@/pages/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeDetail from "@/pages/EmployeeDetail";
import PayrollRuns from "@/pages/PayrollRuns";
import NewPayrollRun from "@/pages/NewPayrollRun";
import PayStubs from "@/pages/PayStubs";
import StubDetail from "@/pages/StubDetail";
import Company from "@/pages/Company";
import Reports from "@/pages/Reports";
import EmployeePortal from "@/pages/EmployeePortal";
import PaymentQueue from "@/pages/PaymentQueue";
import Worksites from "@/pages/Worksites";
import TimeClock from "@/pages/TimeClock";
import AdminTimeEntries from "@/pages/AdminTimeEntries";
import Approvals from "@/pages/Approvals";
import AuditLog from "@/pages/AuditLog";
import Billing from "@/pages/Billing";
import ResourcesPage from "@/pages/Resources";
import TimeOff from "@/pages/TimeOff";
import TimesheetEdit from "@/pages/TimesheetEdit";
import PayrollCalendar from "@/pages/PayrollCalendar";
import YearEnd from "@/pages/YearEnd";
import LegalTerms from "@/pages/legal/LegalTerms";
import LegalPrivacy from "@/pages/legal/LegalPrivacy";
import LegalRetention from "@/pages/legal/LegalRetention";
import LegalDisclaimer from "@/pages/legal/LegalDisclaimer";
import ApiDocs from "@/pages/ApiDocs";
import PublicW9 from "@/pages/PublicW9";
import Public1099 from "@/pages/Public1099";
import CookieBanner from "@/components/CookieBanner";
import CheckmateBuddy from "@/components/CheckmateBuddy";
import WorkflowSettings from "@/pages/WorkflowSettings";
import ComplianceCalendar from "@/pages/ComplianceCalendar";
import AuthCallback from "@/pages/AuthCallback";
import ComplianceVault from "@/pages/ComplianceVault";
import EstateView from "@/pages/EstateView";
import TaxTables from "@/pages/TaxTables";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center font-mono text-sm">LOADING…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app/portal" replace />;
  return children;
}

// ProOnly wraps AdminOnly + a live check against `/billing/status`. Non-admins
// are bounced to /app/portal; admins on a non-Pro plan get sent to /app/billing
// with a helpful toast — the page they were trying to reach is a paid feature.
function ProOnly({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, active: false });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await import("@/lib/api").then((m) => m.api.get("/billing/status"));
        if (!cancelled) setState({ loading: false, active: !!data?.pro?.active });
      } catch {
        if (!cancelled) setState({ loading: false, active: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (user === null || state.loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app/portal" replace />;
  if (!state.active) return <Navigate to="/app/billing" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={TOASTER_OPTIONS} />
        <UpgradeBannerProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/legal/terms" element={<LegalTerms />} />
          <Route path="/legal/privacy" element={<LegalPrivacy />} />
          <Route path="/legal/retention" element={<LegalRetention />} />
          <Route path="/legal/disclaimer" element={<LegalDisclaimer />} />
          {/* Legacy legal URLs — keep every old link and email alive */}
          <Route path="/disclaimer" element={<Navigate to="/legal/disclaimer" replace />} />
          <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/docs/api" element={<ApiDocs />} />
          <Route path="/w9/:token" element={<PublicW9 />} />
          <Route path="/1099/:token" element={<Public1099 />} />
          <Route path="/auth/google-callback" element={<AuthCallback />} />
          {/* Legacy / marketing aliases → funnel into the protected console */}
          <Route path="/admin" element={<Protected><Navigate to="/app" replace /></Protected>} />
          <Route path="/admin/*" element={<Protected><Navigate to="/app" replace /></Protected>} />
          <Route path="/console" element={<Protected><Navigate to="/app" replace /></Protected>} />
          <Route path="/console/*" element={<Protected><Navigate to="/app" replace /></Protected>} />
          <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="employees" element={<AdminOnly><Employees /></AdminOnly>} />
            <Route path="employees/:id" element={<AdminOnly><EmployeeDetail /></AdminOnly>} />
            <Route path="payroll" element={<AdminOnly><PayrollRuns /></AdminOnly>} />
            <Route path="payroll/new" element={<AdminOnly><NewPayrollRun /></AdminOnly>} />
            <Route path="paystubs" element={<PayStubs />} />
            <Route path="paystubs/:id" element={<StubDetail />} />
            <Route path="payments" element={<AdminOnly><PaymentQueue /></AdminOnly>} />
            <Route path="worksites" element={<ProOnly><Worksites /></ProOnly>} />
            <Route path="time-entries" element={<AdminOnly><AdminTimeEntries /></AdminOnly>} />
            <Route path="approvals" element={<AdminOnly><Approvals /></AdminOnly>} />
            <Route path="audit" element={<AdminOnly><AuditLog /></AdminOnly>} />
            <Route path="billing" element={<AdminOnly><Billing /></AdminOnly>} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="clock" element={<TimeClock />} />
            <Route path="time-off" element={<TimeOff />} />
            <Route path="timesheets" element={<TimesheetEdit />} />
            <Route path="payroll-calendar" element={<PayrollCalendar />} />
            <Route path="year-end" element={<ProOnly><YearEnd /></ProOnly>} />
            <Route path="workflow-settings" element={<ProOnly><WorkflowSettings /></ProOnly>} />
            {/* Legacy alias — /billing-concierge now lives inside the Office Manager tabs */}
            <Route path="billing-concierge" element={<Navigate to="/app/workflow-settings#billing" replace />} />
            <Route path="company" element={<AdminOnly><Company /></AdminOnly>} />
            <Route path="reports" element={<AdminOnly><Reports /></AdminOnly>} />
            <Route path="tax-tables" element={<AdminOnly><TaxTables /></AdminOnly>} />
            <Route path="calendar" element={<AdminOnly><ComplianceCalendar /></AdminOnly>} />
            <Route path="portal" element={<EmployeePortal />} />
            {/* Hidden — no nav entry. Non-root accounts get a 404 shell. */}
            <Route path="compliance-history" element={<AdminOnly><ComplianceVault /></AdminOnly>} />
            <Route path="estate" element={<AdminOnly><EstateView /></AdminOnly>} />
            <Route path="*" element={
              <div className="font-mono p-6" data-testid="app-not-found">
                <h1 className="text-4xl font-black">404</h1>
                <p className="text-sm mt-2">That page does not exist.</p>
                <a href="/app" className="inline-block mt-4 border-2 border-ink px-4 py-2 text-xs uppercase hover:bg-ink hover:text-white transition-colors">Back to dashboard</a>
              </div>
            } />
          </Route>
        </Routes>
        <CookieBanner />
        <CheckmateBuddy />
        </UpgradeBannerProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
